import React, { useState } from 'react';

import Button from '@amzn/meridian/button';
import { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import useForm, { type Validations } from 'src/hooks/useForm';
import { MAX_TASK_NAME_CHAR_LENGTH } from 'src/store/adminConfigPageSlice/common/constants';
import {
  addOrEditTaskGroup,
  getBlankTaskGroupTableDataRow,
  setIsTaskGroupModalOpen,
  setSelectedTaskGroupTableRow,
  type TaskGroupTableDataRow,
} from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';

import TaskGroupModalForm, { type FormDataProps } from './taskGroupModalForm';

// **** Constants **** //

const VALIDATIONS: Validations<FormDataProps> = {
  label: {
    required: {
      value: true,
      message: 'Group name is required',
    },
    custom: {
      isValid: (value) => value.length <= MAX_TASK_NAME_CHAR_LENGTH,
      message: `Group name must be less than or equal to ${MAX_TASK_NAME_CHAR_LENGTH} characters.`,
    },
  },
  region: {
    required: {
      value: true,
      message: 'Region is required',
    },
  },
};

// **** Components **** //

/**
 * TaskGroupModal Content
 */
const TaskGroupModalContent = () => {
  const dispatch = useAppDispatch();
  const { selectedTaskGroupTableRow } = useAppSelector((state) => state.taskConfigPage);
  const [showSpinner, setShowSpinner] = useState(false);

  // Handle the task-group form
  const form = useForm<FormDataProps>({
    validations: { ...VALIDATIONS },
    initialValues: {
      label: selectedTaskGroupTableRow?.label ?? '',
      region: selectedTaskGroupTableRow?.region ?? '',
      tasks: selectedTaskGroupTableRow?.tasks ?? [],
    },
  });

  // addOrEdit TaskGroup
  const onSave = async () => {
    if (form.validateForm()) {
      setShowSpinner(true);
      const updatedData: TaskGroupTableDataRow = {
        ...getBlankTaskGroupTableDataRow(),
        ...selectedTaskGroupTableRow,
        ...form.formData,
      };
      try {
        await dispatch(addOrEditTaskGroup(updatedData)).unwrap();
        dispatch(setIsTaskGroupModalOpen(false));
        dispatch(setSelectedTaskGroupTableRow(null));
        form.resetForm();
      } finally {
        setShowSpinner(false);
      }
    }
  };

  // Close modal
  const onCancel = () => {
    dispatch(setIsTaskGroupModalOpen(false));
    dispatch(setSelectedTaskGroupTableRow(null));
    form.resetForm();
  };

  return (
    <>
      <LoadingSpinnerWrapper showSpinner={showSpinner}>
        <TaskGroupModalForm form={form} />
      </LoadingSpinnerWrapper>
      <ModalFooter>
        <Row alignmentHorizontal="end">
          <Button disabled={showSpinner} type="secondary" size="medium" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={showSpinner} type="primary" size="medium" onClick={onSave}>
            Save
          </Button>
        </Row>
      </ModalFooter>
    </>
  );
};

// **** Export **** //

export default TaskGroupModalContent;
